import React from 'react';
import { BedIcon, ClipboardListIcon, LockIcon, MicroscopeIcon, SparklesIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { CtaButton } from '../CtaButton';
import { Reveal } from '../Reveal';
const STEPS = [{
  icon: ClipboardListIcon,
  day: 'Day 1',
  title: 'You arrive. We design your smile on your face.',
  body: 'Records, full clinical examination, CBCT 3D scan, intraoral scan, and facial photographs. Before any implant is placed, your smile is designed digitally and approved on your own face.',
  highlight: 'You approve the design before treatment begins.'
}, {
  icon: MicroscopeIcon,
  day: 'Day 2',
  title: 'Guided surgery, planned in software before you land.',
  body: 'Four to six implants placed through a printed surgical guide, never freehand. You leave the chair with a fixed provisional smile you wear for the rest of your trip.',
  highlight: 'Local anesthesia. Awake, numb, and no added recovery risk.'
}, {
  icon: BedIcon,
  day: 'Days 3 and 4',
  title: 'Rest, recovery, follow-up. Adjustments as needed.',
  body: 'Daily clinical check-ins, post-op review, comfort, speech, and bite verification. Most patients describe Days 3 and 4 as the moment they stop rehearsing their smile and start to feel like themselves again.',
  highlight: 'Your hotel is ten minutes from the clinic.'
}, {
  icon: SparklesIcon,
  day: 'Day 5',
  title: 'Final prosthesis delivered. You fly home smiling.',
  body: 'Fit, esthetics, speech, and function are verified one last time. Your final prosthesis is delivered, and you fly home the same day with it already in place.',
  highlight: 'No second trip four to six months later.'
}];
export function Mechanism() {
  return <section className="w-full bg-white py-20 lg:py-28" data-id="element-145" data-mp="2ugol5ple6w5n">
      <div className="mx-auto max-w-6xl px-6" data-id="element-146" data-mp="2ui9ghhfj6j4k">
        <SectionHeading eyebrow="Exactly what happens in those five days" title={<><span data-id="element-148" data-mp="2zd6m0edlqacp">This is not &ldquo;teeth in a day.&rdquo; </span><span className="text-[#1F6E7B]" data-id="element-149" data-mp="ze1oebeac9vi">It is your final smile in 5 days.</span></>} data-id="element-147" data-mp="2zezvs2jihaox">
          <p data-id="element-150" data-mp="ueykiua1k9p4">
            Pler Smile Dadeland built a four step protocol that price driven clinics and speed driven clinics
            cannot replicate. Surgery is performed under local anesthesia. You remain awake and fully numb in
            the surgical area, with no general anesthesia, no IV sedation, and no added recovery risk.
          </p>
        </SectionHeading>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2" data-id="element-151" data-mp="1e2fyn7e170m3">
          {STEPS.map((step, index) => {
          const Icon = step.icon;
          return <Reveal key={step.day} delay={index * 0.08} className="h-full" data-id="element-152" data-mp="1rzurkm3htqit" data-mpi={index}>
                <article className="flex h-full flex-col rounded-[1.75rem] border border-[#A9DFE9]/60 bg-white p-6 shadow-soft transition duration-300 ease-out hover:-translate-y-1 hover:border-[#1F6E7B]/40 hover:shadow-lg sm:p-8" data-id="element-153" data-mp="1n3dxzcdiyjyc">
                  <div className="flex items-center justify-between" data-id="element-154" data-mp="24c6ni3iczyek">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#A9DFE9]/40 text-[#1F6E7B]" data-id="element-155" data-mp="24cyjhffvby5d">
                      <Icon size={24} aria-hidden="true" data-id="element-156" data-mp="1umddh6xv7rcf" />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#1F6E7B]" data-id="element-157" data-mp="1ukap0rcd269t">Step {index + 1}</span>
                  </div>
                  <p className="mt-5 text-sm font-semibold uppercase tracking-[0.12em] text-[#1A1A2E]/60" data-id="element-158" data-mp="1llfi1d1hss5g">{step.day}</p>
                  <h3 className="mt-2 text-xl font-bold leading-snug text-[#1A1A2E]" data-id="element-159" data-mp="1qljnm80v42gw">{step.title}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#1A1A2E]/80" data-id="element-160" data-mp="1boswsn597z2w">{step.body}</p>
                  <p className="mt-5 rounded-xl bg-[#A9DFE9]/25 px-4 py-3 text-sm font-semibold leading-relaxed text-[#1F6E7B]" data-id="element-161" data-mp="1gndpm1i6fghb">{step.highlight}</p>
                </article>
              </Reveal>;
        })}
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-sm italic leading-relaxed text-[#1A1A2E]/70" data-id="element-162" data-mp="3ay993yuwgfyv">
          After you fly home, a virtual follow-up with the Dadeland team is scheduled at approximately
          four months. You remain a Pler Smile patient for the full three years of the guarantee.
        </p>

        <Reveal data-id="element-163" data-mp="wyuu8ejzlkm9">
          <figure className="mx-auto mt-16 max-w-3xl text-center" data-id="element-164" data-mp="wze2t4nqywnm">
            <blockquote className="text-2xl font-bold leading-snug text-[#1A1A2E] md:text-3xl" data-id="element-165" data-mp="rzhwuzvhov5f">
              &ldquo;We offer the only smile that actually belongs on your face.&rdquo;
            </blockquote>
          </figure>
        </Reveal>

        <div className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl bg-[#A9DFE9]/25 p-6 text-[15px] leading-relaxed text-[#1A1A2E]/85" data-id="element-166" data-mp="j899ekaz30lq">
          <LockIcon size={20} className="mt-0.5 shrink-0 text-[#1F6E7B]" data-id="element-167" data-mp="990k44r1ya5p" />
          <p data-id="element-168" data-mp="988l0jy8xuao">
            Only <span className="font-semibold text-[#1A1A2E]" data-id="element-169" data-mp="9autqmwvifuh">10 new cases per month</span> access the
            <span className="font-semibold text-[#1A1A2E]" data-id="element-170" data-mp="4c9uoqtcy27m"> 5-Day Smile Experience Program</span> in Dadeland.
            This limit is clinical, not commercial. Every case requires exclusive booking of the surgical team.
          </p>
        </div>

        <div className="mt-9 flex flex-col items-center" data-id="element-171" data-mp="457vny5prbwn">
          <CtaButton label="Reserve My Free Consultation" data-id="element-172" data-mp="939a25s72n1s" />
          <p className="mt-3 text-sm font-medium text-[#1A1A2E]/70" data-id="element-173" data-mp="92rap0a8jspn">You can confirm eligibility before traveling.</p>
        </div>
      </div>
    </section>;
}